import moment from 'moment';
import { BACK_END_TIME_FORMAT, USER_TIME_FORMAT } from 'shared/constants/date';
import type { DayOfWeek } from 'services/api';
import type { TeacherWorkloadWorkDay } from 'shared/api';
import type { TeacherWorkloadWorkDayFormRow } from '../types';
import { WORK_DAYS } from '../model/work-days-config';

const DEFAULT_START = '09:00';
const DEFAULT_END = '16:00';

const apiTimeToFormTime = (time: string) => {
  const parsed = moment(time, [BACK_END_TIME_FORMAT, USER_TIME_FORMAT], true);
  return parsed.isValid() ? parsed.format(USER_TIME_FORMAT) : time;
};

const isValidIntervalStrings = (startTime: string, endTime: string) => {
  const start = moment(startTime, USER_TIME_FORMAT, true);
  const end = moment(endTime, USER_TIME_FORMAT, true);
  return start.isValid() && end.isValid() && start.isBefore(end);
};

export const createDefaultWorkDaysForm = (): TeacherWorkloadWorkDayFormRow[] =>
  WORK_DAYS.map(({ value: dayOfWeek }) => ({
    dayOfWeek,
    mode: 'unlimited',
    startTime: '',
    endTime: '',
  }));

export const apiWorkDaysToForm = (
  api: TeacherWorkloadWorkDay[] | null | undefined,
): TeacherWorkloadWorkDayFormRow[] => {
  const slots = api ?? [];
  const byDay = new Map<DayOfWeek, TeacherWorkloadWorkDay>(
    slots.map((slot) => [slot.dayOfWeek, slot]),
  );

  return WORK_DAYS.map(({ value: dayOfWeek }) => {
    const slot = byDay.get(dayOfWeek);
    if (!slot) {
      return { dayOfWeek, mode: 'off', startTime: '', endTime: '' };
    }
    if (slot.unlimited === true) {
      return { dayOfWeek, mode: 'unlimited', startTime: '', endTime: '' };
    }
    if (slot.startTime && slot.endTime) {
      return {
        dayOfWeek,
        mode: 'interval',
        startTime: apiTimeToFormTime(slot.startTime),
        endTime: apiTimeToFormTime(slot.endTime),
      };
    }
    return { dayOfWeek, mode: 'off', startTime: '', endTime: '' };
  });
};

export const formWorkDaysToApi = (
  rows: TeacherWorkloadWorkDayFormRow[],
): TeacherWorkloadWorkDay[] => {
  const out: TeacherWorkloadWorkDay[] = [];
  for (const row of rows) {
    if (row.mode === 'off') continue;
    if (row.mode === 'unlimited') {
      out.push({ dayOfWeek: row.dayOfWeek, unlimited: true });
      continue;
    }
    if (!row.startTime || !row.endTime) continue;
    out.push({
      dayOfWeek: row.dayOfWeek,
      startTime: row.startTime,
      endTime: row.endTime,
    });
  }
  return out;
};

export const applyWorkDayModeChange = (
  row: TeacherWorkloadWorkDayFormRow,
  mode: TeacherWorkloadWorkDayFormRow['mode'],
): TeacherWorkloadWorkDayFormRow => {
  if (mode === 'interval') {
    const keep =
      row.mode === 'interval' && isValidIntervalStrings(row.startTime, row.endTime)
        ? { startTime: row.startTime, endTime: row.endTime }
        : { startTime: DEFAULT_START, endTime: DEFAULT_END };
    return { ...row, mode, ...keep };
  }
  return { ...row, mode, startTime: '', endTime: '' };
};
